import Autoplay from 'embla-carousel-autoplay';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useDotButton } from '~/hooks/useDotButton';
import { usePrevNextButtons } from '~/hooks/usePrevNextButtons';

import { userStore } from '~/stores';

import { CarouselIcon } from '../../CommonCarousel';

import { createCategoryUrl } from '../utils';

import styles from './BannerMainBlock.module.scss';

interface Banner {
  src: string;
  alt: string;
  linkId: string | number;
}

interface BannerMainBlockProps {
  banners: Banner[];
  isMobile: boolean;
}

const BannerMainBlock = ({ banners, isMobile }: BannerMainBlockProps) => {
  const { handleClickCategory } = useCategoryAnalytics();
  const [api, setApi] = useState<CarouselApi | undefined>(undefined);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(api);
  const { onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(api);

  const shouldLoop = banners.length > 1;
  const isPrevHidden = isMobile || banners.length === 1;
  const isNextHidden = isMobile || banners.length === 1;

  const autoplayPlugin = useMemo(
    () =>
      Autoplay({
        delay: 5000,
        stopOnInteraction: false,
      }),
    [],
  );

  const carouselOpts = useMemo(() => {
    if (isMobile) {
      return {
        loop: shouldLoop,
        align: 'center' as const,
        slidesToScroll: 1,
        active: banners.length > 1,
      };
    }
    return {
      loop: shouldLoop,
      align: 'start' as const,
      slidesToScroll: 1,
      active: banners.length > 1,
    };
  }, [shouldLoop, isMobile, banners.length]);

  if (!banners || banners.length === 0) {
    return null;
  }

  return (
    <div className={classes([styles.container, 'banner-carousel carousel'])}>
      <CarouselIcon
        hide={isPrevHidden}
        position="left"
        variant="fill"
        onClick={onPrevButtonClick}
      />
      <Carousel
        setApi={setApi}
        opts={carouselOpts}
        plugins={[autoplayPlugin]}
        className={styles.carousel}
        dir={userStore.dir}
        key={userStore.dir}
      >
        <CarouselContent>
          {banners.map(({ src, alt, linkId }) => (
            <CarouselItem
              style={{
                marginRight: `${isMobile ? 6 : 10}px`,
              }}
              className="banner-carousel__item"
              key={src}
            >
              <Link
                data-link={`banner-${linkId}`}
                onClick={handleClickCategory(linkId)}
                to={createCategoryUrl({ id: linkId })}
              >
                <CloudflareResponseImage
                  src={src}
                  srcSetParams={defaultSrcSetParams}
                  alt={alt}
                  loading="lazy"
                />
              </Link>
            </CarouselItem>
          ))}
        </CarouselContent>
        {isMobile && (
          <div className={styles.pagination}>
            {scrollSnaps.map((_, index) => (
              <span
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={classes([
                  styles.paginationDot,
                  index === selectedIndex && styles.active,
                ])}
              />
            ))}
          </div>
        )}
      </Carousel>
      <CarouselIcon
        hide={isNextHidden}
        position="right"
        variant="fill"
        onClick={onNextButtonClick}
      />
    </div>
  );
};

export default observer(BannerMainBlock);
